/* istanbul ignore file */
import { defineStore } from 'pinia';
import { ref, computed, shallowRef } from 'vue';

// Assuming other stores will be converted to Pinia as well
/* Pinia is generally called stores instead of store. 
This is to emphasize that Pinia uses multiple stores, instead of a single store in Vuex.

There is no need to dynamically register modules in Pinia. 
Stores are dynamic by design and are only registered when they are needed. 
If a store is never used, it will never be "registered". */

// EX in vue file: import { useVisualFilterStore } from './visualFilter';
/* prev store modules:
import modalOverlayStore from '@common-vue/atomic-ui/src/store_modules/modalOverlay/store';
import visualFilterStore from '@common-vue/canvas-aem/src/store_modules/visualFilter/store';
import recommendationStore from '@common-vue/pros-collection/src/store_modules/store';
import imageMapStore from '@common-vue/canvas-aem/src/store_modules/imageMap/store';
import googleAdStore from '@common-vue/site-monetization/src/modules/googleAds/store';
import sponsoredItemsStore from '@common-vue/site-monetization/src/modules/sponsoredItems/store';
import productQuickviewStore from '@common-vue/product-quickview/src/store/productQuickviewStore';
import traitsStore from '@common-vue/media-card-large/src/store_modules/traitsStore';
import slideoutStore from '@common-vue/atomic-ui/src/store_modules/slideout/store';
import { locations } from '@common-vue/locator/src/store';
import FilteringFacetsStore from '@components/FilteringFacets/store'; */

export const usePageDataStore = defineStore('pageDataStore', () => {
  // --- COMPOSABLES ---
  const brand = import.meta.env.VITE_ENTRY_POINT;
  const config = useRuntimeConfig();
  const isServerOptimizationEnabled = config.isServerOptimizationEnabled;
  const ssrRef = import.meta.server && isServerOptimizationEnabled ? shallowRef : ref;

  // --- STATE ---
  const pageData = ssrRef({});
  const pagination = ssrRef([
    { key: 'Sortby', value: 'ORIGINAL' },
    { key: 'Pageindex', value: 1 },
  ]);
  const pzoEligibleItems = ssrRef({});
  const imageHoveringData = ref(null);
  const selectedColor = ref(null);
  const altGridViewProdsPerRow = ref(null);

  // --- GETTERS (Computed) ---
  const getRowSortableGrid = computed(() => pageData.value?.body?.canvas?.rows?.find((item) => !!item.rowSortableGrid)?.rowSortableGrid);

  const getSortableGrid = computed(() => getRowSortableGrid.value?.zones?.[1]?.sortableGrid ?? {});

  const getCategoryTree = computed(() => getRowSortableGrid.value?.zones?.[0]?.categoryTree ?? pageData.value?.body?.categoryTree ?? {});

  const getFacets = computed(() => getRowSortableGrid.value?.zones?.[0]?.facets?.facets || []);

  const getSelectedFacets = computed(() => getRowSortableGrid.value?.zones?.[0]?.facets?.meta?.selectedFacets || {});

  const getFacetsMetaSelected = computed(() => getRowSortableGrid.value?.zones?.[0]?.facets?.meta?.selected || []);

  const getFacetsSelected = computed(() => {
    const selected = getRowSortableGrid.value?.zones?.[0]?.facets?.meta?.selected;
    if (!selected) return [];
    return selected.reduce((selectedValues, facet) => {
      selectedValues[facet.name] = facet.values.map((item) => item.value);
      return selectedValues;
    }, {});
  });

  const getMeta = computed(() => pageData.value?.meta);

  const getMetaProperties = computed(() => getMeta.value?.properties ?? {});

  const getXMacysApiKey = computed(() => pageData.value?.xMacysApiKey);

  const getFacetMetaBaseUrl = computed(() => {
    const uniqueSearchPatternEnabled = getMetaProperties.value?.uniqueSearchPatternEnabled;
    const facetMeta = getRowSortableGrid.value?.zones?.[0]?.facets?.meta;
    const metaBaseUrl = facetMeta?.baseUrl;

    const isSearchToFeatured = uniqueSearchPatternEnabled && metaBaseUrl?.startsWith('/shop/search');
    const isSearch = pageData.value?.meta?.pageType?.search;

    let baseUrl = metaBaseUrl;
    if (isSearch && isSearchToFeatured) {
      const queryParams = new URLSearchParams(facetMeta?.queryString || '');
      const keyword = queryParams.get('keyword');
      const encoded = encodeSearchKeyword(keyword, brand === 'bcom');
      baseUrl = `/shop/featured/${encoded}`;
    }

    return baseUrl;
  });

  const getSelectedFacetUrl = computed(() => {
    let selectedFacetUrl = getRowSortableGrid.value?.zones?.[0]?.facets?.meta?.selectedFacetUrl;
    if (typeof selectedFacetUrl === 'string') {
      selectedFacetUrl = selectedFacetUrl.replace(/\|/g, '%7C');
    }
    return selectedFacetUrl;
  });

  const getAnalyticsData = computed(() => pageData.value?.meta?.analytics?.data);

  const getModel = computed(() => getSortableGrid.value?.model ?? {});
  const getModelPageIndex = computed(() => getModel.value.pagination?.currentPage || 1); // xapi model value

  const getProductCount = computed(() => getRowSortableGrid.value?.zones?.[0].facets?.meta?.productCount || 0);

  const getDisableDeliveryAndFulfillmentFacet = computed(() => getMeta.value?.pageType?.disableDeliveryAndFulfillmentFacet || false);

  const getSelectedFacetCount = computed(() => {
    const selectedFacets = getSelectedFacets.value;
    return Object.values(selectedFacets).flatMap?.((values) => values)?.length || 0;
  });

  const getFacetsMeta = computed(() => getRowSortableGrid.value?.zones?.[0].facets?.meta);

  const getSortOptions = computed(() => {
    const model = getModel.value || null;
    let selected = null;
    let options = [];
    if (!model || !model.sort) return { options, selected };
    options = model.sort.map(({ value, name, isSelected }) => {
      const option = { value, displayValue: name };
      if (isSelected) {
        selected = option;
      }
      return option;
    });
    return { options, selected };
  });

  const getSortOptionsForFacets = computed(() => {
    const model = getModel.value || null;
    return (
      model?.sort?.map((item) => ({
        ...item,
        displayValue: item.name,
      })) || []
    );
  });

  const getCurrentSortby = computed(() => pagination.value?.find((item) => item.key === 'Sortby')?.value ?? 'ORIGINAL');
  const getCurrentPageindex = computed(() => pagination.value?.find((item) => item.key === 'Pageindex')?.value ?? 1);

  const getPathUrls = computed(() =>
    pagination.value
      .filter((item) => !((item.key === 'Sortby' && item.value === 'ORIGINAL') || (item.key === 'Pageindex' && item.value === 1)))
      .map(({ key, value }) => ({ key, value })),
  );

  const getPagination = computed(() => {
    const model = getModel.value || null;
    let pageIndex = getCurrentPageindex.value;
    let sortBy = getCurrentSortby.value;

    if (!model || !model.pagination) return { pageIndex, sortBy };

    if (brand === 'bcom' && model.pagination?.numbers?.length) {
      const currentNumber = model.pagination.numbers.find(
        (num) => num.current && (num.url.includes('Upc_bops_purchasable') || num.url.includes('Upc_sdd_purchasable')),
      )?.name;
      model.pagination.currentPage = Number(currentNumber ?? model.pagination.numbers.find((num) => num.current)?.name) || 0;
    }

    pageIndex = model.pagination.currentPage === 0 ? pageIndex : (model.pagination.currentPage ?? 1);
    const selected = model.sort.find(({ isSelected }) => isSelected);
    sortBy = selected ? selected.value : 'ORIGINAL';

    return { pageIndex, sortBy };
  });

  const getPzoItems = computed(() => pzoEligibleItems.value?.List?.map((x) => x.productId) || []);

  const getCollection = computed(() => (pageData.value?.redirect ? [] : (getSortableGrid.value?.collection ?? [])));

  const isRedirected = computed(() => !!pageData.value?.redirect);

  const getProductIds = computed(() => getCollection.value.map((val) => val.product?.id));

  const getIsDomestic = computed(() => pageData.value.isDomestic);

  const getExperimentIds = computed(() => {
    // This logic might need adjustment depending on how you handle experiments in Pinia
    const sourceExperimentIds = pageData.value?.meta?.context?.customerExperiment?.split(',').map((id) => id.trim()) || [];
    const SSCExperimentIds = pageData.value?.SSCExperimentIds || [];
    // Assuming combineExperimentIds is a utility function you have
    // import combineExperimentIds from '@utils/combineExperimentIds';
    return [...new Set([...sourceExperimentIds, ...SSCExperimentIds])]; // Simplified version
  });

  const getConvoSearchResult = computed(() => pageData.value?.body?.conversationalSearchResult);
  const getConversationId = computed(() => getConvoSearchResult.value?.conversationId);
  const getAltGridViewProdsPerRow = computed(() => altGridViewProdsPerRow.value);

  // --- ACTIONS ---
  function setPage(payload) {
    // since on the client side we do not send the meta.properties, we want to merge the new payload meta with existing
    // EX:: pageData.meta = { analytics : { a }, properties: { ... }, pageType: { b }, ... }
    // payload.meta = { analytics : { c }, pageType: { d }, ... } <- smaller response
    // new meta value = { analytics : { c }, properties: { ... } pageType: { d }, ... }
    const meta = payload.meta ? { ...pageData.value.meta, ...payload.meta } : pageData.value.meta;
    const newPageData = { ...pageData.value, ...payload, meta };
    // Object.freeze on the server significantly reduces Vue rendering time for large static objects.
    // We freeze the top-level object and its immediate children (body, meta, sysFulfillmentData, etc.)
    // to prevent Vue from recursively walking the tree to create proxies.
    if (import.meta.server && isServerOptimizationEnabled) {
      freezeShallow(newPageData);
    }
    pageData.value = newPageData;
    pagination.value = payload.pagination ?? pagination.value;
  }

  function setPathUrls(payload) {
    const useImmutableUpdate = import.meta.server && isServerOptimizationEnabled;
    // Clone only when required by the shallowRef; otherwise mutate the existing array in place
    const newPagination = useImmutableUpdate ? [...pagination.value] : pagination.value;
    payload.forEach(({ key, value }) => {
      const index = newPagination.findIndex((item) => item.key.toLowerCase() === key.toLowerCase());
      if (index === -1) return;

      if (useImmutableUpdate) {
        newPagination[index] = { ...newPagination[index], value };
      } else {
        newPagination[index].value = value;
      }
    });
    if (useImmutableUpdate) {
      pagination.value = newPagination;
    }
  }

  function setPzoUrl(payload) {
    pzoEligibleItems.value = payload;
  }

  function resetConvoSearchResult() {
    if (pageData.value.body) {
      if (import.meta.server && isServerOptimizationEnabled) {
        const newPageData = {
          ...pageData.value,
          body: {
            ...pageData.value.body,
            conversationalSearchResult: null,
          },
        };
        freezeShallow(newPageData);
        pageData.value = newPageData;
      } else {
        pageData.value.body.conversationalSearchResult = null;
      }
    }
  }

  function freezeShallow(aObjData) {
    Object.freeze(aObjData);
    Object.keys(aObjData).forEach((key) => {
      if (aObjData[key] && typeof aObjData[key] === 'object') {
        Object.freeze(aObjData[key]);
      }
    });
  }

  function setImageHoveringData(payload) {
    imageHoveringData.value = payload;
  }

  function setSelectedColor(payload) {
    selectedColor.value = payload;
  }

  function setAltGridViewProducts(payload) {
    altGridViewProdsPerRow.value = payload;
  }

  function setPaginationRefresh(payload) {
    const { pageIndex, sortBy } = payload;
    pagination.value = [
      { key: 'Sortby', value: sortBy ?? 'ORIGINAL' },
      { key: 'Pageindex', value: pageIndex ?? 1 },
    ];
  }

  return {
    // State
    pageData,
    pagination,
    pzoEligibleItems,
    imageHoveringData,
    selectedColor,
    altGridViewProdsPerRow,
    // Getters
    getRowSortableGrid,
    getSortableGrid,
    getCategoryTree,
    getFacets,
    getSelectedFacets,
    getFacetsMetaSelected,
    getFacetsSelected,
    getMeta,
    getMetaProperties,
    getXMacysApiKey,
    getFacetMetaBaseUrl,
    getSelectedFacetUrl,
    getAnalyticsData,
    getModel,
    getProductCount,
    getDisableDeliveryAndFulfillmentFacet,
    getSelectedFacetCount,
    getFacetsMeta,
    getSortOptions,
    getSortOptionsForFacets,
    getCurrentSortby,
    getCurrentPageindex,
    getPathUrls,
    getPagination,
    getPzoItems,
    getProductIds,
    getCollection,
    getIsDomestic,
    getExperimentIds,
    getConvoSearchResult,
    getConversationId,
    getAltGridViewProdsPerRow,
    isRedirected,
    getModelPageIndex,
    // Actions
    setPage,
    setPathUrls,
    setPzoUrl,
    resetConvoSearchResult,
    setImageHoveringData,
    setSelectedColor,
    setAltGridViewProducts,
    setPaginationRefresh,
  };
});
